import React, { useEffect, useMemo, useRef } from "react";
import {
  lightenHexColor,
  themeButtonBackground,
  themeButtonTextColor,
} from "../../utils/colorUtils";
import "./yogaThemedModal.css";

export default function YogaThemedModal({
  open,
  title,
  titleId = "ytm-title",
  themeData,
  confirmText = "Got it",
  cancelText,
  onConfirm,
  onCancel,
  closeOnBackdrop = true,
  autoCloseMs = 0,
  matchButtonColors = false,
  children,
}) {
  const timerRef = useRef(null);
  const buttonBg = themeButtonBackground(themeData);
  const buttonText = themeButtonTextColor(themeData);
  const popupBg = String(themeData?.popup_background_color || "").trim();
  const popupText = String(themeData?.popup_text_color || "").trim();
  const titleColor = matchButtonColors
    ? popupText || "#ffffff"
    : themeData?.text_color || "#1a2b6d";
  const modalBg = useMemo(() => {
    if (matchButtonColors) return popupBg || "#1B1433";
    if (!buttonBg) return null;
    return lightenHexColor(buttonBg, 0.82);
  }, [buttonBg, matchButtonColors, popupBg]);
  const btnStyle = {
    ...(buttonBg && { backgroundColor: buttonBg }),
    ...(buttonText && { color: buttonText }),
    ...(themeData?.button_bordercolor && { borderColor: themeData.button_bordercolor }),
    ...(buttonText && { border: `2px solid ${buttonText}` }),
  };
  const modalStyle = {
    "--ytm-text-color": titleColor,
    ...(modalBg ? { "--ytm-modal-bg": modalBg } : {}),
  };

  useEffect(() => {
    if (!open || !autoCloseMs || !onConfirm) return undefined;
    timerRef.current = window.setTimeout(onConfirm, autoCloseMs);
    return () => {
      if (timerRef.current != null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [open, autoCloseMs, onConfirm]);

  const handleBackdropClick = () => {
    if (!closeOnBackdrop) return;
    if (onCancel) onCancel();
    else onConfirm?.();
  };

  if (!open) return null;

  return (
    <>
      <div
        className="ytm-backdrop"
        onClick={handleBackdropClick}
        aria-hidden
      />
      <div
        className={`ytm-modal${modalBg ? " ytm-modal--themed" : ""}`}
        role="dialog"
        aria-labelledby={titleId}
        style={modalStyle}
      >
        {autoCloseMs > 0 ? (
          <div className="ytm-timer-bar" aria-hidden>
            <span
              className="ytm-timer-bar-fill"
              style={{
                backgroundColor: buttonBg || "#22c55e",
                animationDuration: `${autoCloseMs}ms`,
              }}
            />
          </div>
        ) : null}
        <h3 id={titleId}>
          {title}
        </h3>
        <div className="ytm-body">{children}</div>
        <div className={cancelText ? "ytm-actions ytm-actions--dual" : "ytm-actions"}>
          {cancelText ? (
            <button
              type="button"
              className="ytm-cancel"
              style={{
                color: titleColor,
                borderColor: buttonBg || titleColor,
              }}
              onClick={onCancel}
            >
              {cancelText}
            </button>
          ) : null}
          <button
            type="button"
            className="ytm-confirm ytm-confirm--themed"
            style={btnStyle.backgroundColor || btnStyle.color ? btnStyle : undefined}
            onClick={onConfirm}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </>
  );
}
